/** @jsx isolateComponent */
import { Fragment, useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import { ServiceIcon } from '@zapier/design-system-patterns';
import { Link, Spacer, Typeahead, TypeaheadItem } from '@zapier/design-system';

import CollapsedBlock from '../CollapsedBlock';
import SkipButton from '../../shared/SkipButton';
import Surface from '../../shared/Surface';
import { App } from './types';
import {
  GET_HELP_PATH,
  DEFAULT_SERVICE_ICON_URL_GREY,
} from '../../../constants';
import { getSelectedApp } from './utils';
import { SizeType } from '../../../types';

type AppBlockProps = {
  /** Title */
  title: string;
  /** Set to `true` while `apps` get loaded, to display a loading state. */
  isLoading: boolean;
  /** When `false` or when there's an error loading apps, the user can continue without an app. */
  isRequired: boolean;
  /** Set to an `Error` when loading failed */
  error?: Error;
  /** List of apps */
  apps: App[];
  /** Function to call when the user clicks 'Edit' in the `closed` or `confirm` state. */
  onClickEdit: () => void;
  /** Function to call with the app the user selected or `null` if they skipped. */
  onSelect: (item: App | null) => void;
  /** The app to display in the `closed` or `confirm` state. This may also be an app's `id` or `null` if the question was skipped. */
  selectedApp?: App | string | null;
  /** State to display */
  state: 'open' | 'closed' | 'confirm';
  /** Size to display. */
  size?: SizeType;
};

const appBlockCss = css`
  align-items: center;
  display: grid;
  grid-gap: 10px;
  grid-template-columns: min-content 1fr;
`;

const contentCss = css`
  display: grid;
  grid-gap: 30px;
`;

const SkipText = ({ onSelect }: Pick<AppBlockProps, 'onSelect'>) => (
  <Fragment>
    {' '}
    Otherwise, please{' '}
    <Link
      href="#"
      onPress={() => {
        onSelect(null);
      }}
    >
      continue without selecting an app
    </Link>{' '}
    and provide the name of the app in your message.
  </Fragment>
);

const Content = ({
  apps,
  error,
  isLoading,
  isRequired,
  onSelect,
}: Pick<
  AppBlockProps,
  'apps' | 'error' | 'isLoading' | 'isRequired' | 'onSelect'
>) => {
  if (error) {
    return (
      <p>
        We could not load apps due to the error "{error.message}". Please come
        back later or <SkipText onSelect={onSelect} />
      </p>
    );
  }

  if (!isLoading && apps.length === 0) {
    return (
      <Fragment>
        <p>
          We couldn't find an app that you maintain on this account. If you have
          a question about an app that you do not maintain, please{' '}
          <Link
            href={`${GET_HELP_PATH}?topic=zapier&zapier=feature`}
            target="_self"
          >
            continue here
          </Link>
          .
          {!isRequired && (
            <Fragment>
              {' '}
              Otherwise, please <SkipText onSelect={onSelect} />
            </Fragment>
          )}
        </p>
      </Fragment>
    );
  }

  const [inputValue, setInputValue] = useState('');

  const filteredApps = inputValue
    ? apps
        .filter(app =>
          app.name.toLowerCase().includes(inputValue.toLowerCase())
        )
        .slice(0, 10)
    : [];

  return (
    <div css={contentCss}>
      <Typeahead
        aria-label="App"
        isDisabled={isLoading}
        inputValue={inputValue}
        onInputChange={setInputValue}
        selectedKey={null}
        onChange={key => {
          const app = apps.find(a => a.id === key);
          onSelect(app ?? null);
        }}
        placeholder={isLoading ? 'Loading apps...' : 'Search for your App'}
        size="medium"
      >
        {filteredApps.map(app => (
          <TypeaheadItem key={app.id} id={app.id} textValue={app.name}>
            <ServiceIcon
              alt={app.name}
              serviceName={app.id}
              size={30}
              src={app.src || DEFAULT_SERVICE_ICON_URL_GREY}
            />
            {app.name}
          </TypeaheadItem>
        ))}
      </Typeahead>
      {!isRequired && (
        <SkipButton onSelect={onSelect}>
          I don’t need help with a specific app
        </SkipButton>
      )}
    </div>
  );
};

/**
 * Renders a block with a typeahead searchbox to select an app.
 */
const AppBlock = ({
  onClickEdit,
  size = 'large',
  state,
  title,
  ...otherProps
}: AppBlockProps) => {
  if (state === 'closed' || state === 'confirm') {
    const selectedApp = getSelectedApp(otherProps);
    const isDimmed = state !== 'confirm';

    return (
      <CollapsedBlock
        isDimmed={isDimmed}
        onClickEdit={onClickEdit}
        size={size}
        title={title}
      >
        {selectedApp ? (
          <div css={appBlockCss}>
            <ServiceIcon
              alt={selectedApp.name}
              serviceName={selectedApp.id}
              size={30}
              src={selectedApp.src || DEFAULT_SERVICE_ICON_URL_GREY}
            />{' '}
            <span>{selectedApp.name}</span>
          </div>
        ) : (
          <p>You have not selected an app.</p>
        )}
      </CollapsedBlock>
    );
  }

  return (
    <Surface size={size} title={title}>
      <Spacer height={20} />
      <Content {...otherProps} />
    </Surface>
  );
};

/** @component */
export default AppBlock;
